/**
 * Who the state restored from durable storage belongs to.
 *
 * A page refresh replays the whole persisted workspace, and the app cannot tell
 * from the replay alone whether the user reopened the same model or switched to
 * another one. Wiping project-scoped state on every hydration answers that by
 * throwing it away: a connector application that failed half-way leaves the
 * revision in `recoveryRequired` together with the undo checkpoint that is the
 * only way out of it, and both are gone after one reload — the lock silently
 * reopens the forward steps it was raised to block.
 *
 * So ownership is reconciled instead of assumed. Every restored record that can
 * name a document is checked against the active one, and the state is reset only
 * when one of them provably names a different project. Nothing named is not a
 * project switch; an unknown active document is not a project switch either.
 */

import type { ConnectorParameterUndoPlan } from '../../types/parameterSetupUndo';
import type { VerifiedSetupBaseline } from './verifiedBaseline';

/** What was found to belong to another project. */
export type ProjectScopeResetReason =
  | 'foreign-revision'
  | 'foreign-baseline'
  | 'foreign-checkpoint';

export type ProjectScopeReconciliation =
  | { kind: 'adopt' }
  | { kind: 'reset'; reason: ProjectScopeResetReason };

/** The project-scoped records a hydration restores. */
export interface HydratedProjectScope {
  /** Project the persisted setup revision was recorded against. */
  setupRevisionProjectKey: string | null | undefined;
  /** The last setup a read-back confirmed, with the document it confirmed it on. */
  verifiedSetupBaseline: VerifiedSetupBaseline | null | undefined;
  /** Restored undo checkpoints, in any order; absent entries are ignored. */
  undoPlans: readonly (ConnectorParameterUndoPlan | null | undefined)[];
}

const adopt: ProjectScopeReconciliation = { kind: 'adopt' };

const resetBecause = (reason: ProjectScopeResetReason): ProjectScopeReconciliation => ({
  kind: 'reset',
  reason,
});

/**
 * A key names a project only when it carries one. Blank keys are written by
 * records that predate project scoping, and they claim nothing.
 */
const namedProject = (value: string | null | undefined): string | null => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed ? trimmed : null;
};

/** True only when the record names a project and that project is a different one. */
const namesAnotherProject = (
  candidate: string | null | undefined,
  activeProjectKey: string
): boolean => {
  const named = namedProject(candidate);
  return named !== null && named !== activeProjectKey;
};

/**
 * Decides whether hydrated project-scoped state may be adopted as-is.
 *
 * Project keys are compared exactly, the same way document identities are: both
 * sides are produced by `resolveActiveProjectKey`, so a difference in the key is
 * a difference in the document, never a difference in spelling.
 */
export const reconcileHydratedProjectScope = (
  activeProjectKey: string | null | undefined,
  scope: HydratedProjectScope
): ProjectScopeReconciliation => {
  const active = namedProject(activeProjectKey);
  if (!active) {
    return adopt;
  }

  if (namesAnotherProject(scope.setupRevisionProjectKey, active)) {
    return resetBecause('foreign-revision');
  }

  if (namesAnotherProject(scope.verifiedSetupBaseline?.identity.projectKey, active)) {
    return resetBecause('foreign-baseline');
  }

  const foreignCheckpoint = scope.undoPlans.some((plan) =>
    namesAnotherProject(plan?.projectKey, active)
  );

  return foreignCheckpoint ? resetBecause('foreign-checkpoint') : adopt;
};
